import { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { Alert, InputAdornment } from "@mui/material";
import { useNavigate } from "react-router";
import ErrorBanner from "components/Common/ErrorBanner/ErrorBanner.tsx";
import TableSkeleton from "components/Common/Table/TableSkeleton/TableSkeleton.tsx";
import {
	useCloseSupportCaseMutation,
	useEscalateSupportCaseMutation,
	useGetSupportCasesQuery
} from "features/Support/api/supportApi.ts";
import { SupportCase } from "features/Support/api/supportTypes.ts";
import EscalateCaseDialog from "./EscalateCaseDialog.tsx";
import SupportCasesTable from "./SupportCasesTable.tsx";
import * as Styles from "./SupportCases.styles.ts";

const SEARCH_DELAY_MS = 350;
const MINIMUM_SEARCH_LENGTH = 3;

const SupportCases = () => {
	const navigate = useNavigate();
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState<string>();
	const [caseToEscalate, setCaseToEscalate] = useState<SupportCase>();
	const [actionCaseId, setActionCaseId] = useState<string>();
	const [actionError, setActionError] = useState(false);
	const trimmedSearch = searchInput.trim();
	const hasSearchError = trimmedSearch.length > 0 && trimmedSearch.length < MINIMUM_SEARCH_LENGTH;

	useEffect(() => {
		if (trimmedSearch.length < MINIMUM_SEARCH_LENGTH) {
			setSearch(undefined);
			return;
		}

		const timeoutId = window.setTimeout(() => setSearch(trimmedSearch), SEARCH_DELAY_MS);
		return () => window.clearTimeout(timeoutId);
	}, [trimmedSearch]);

	const { data, isError, isFetching } = useGetSupportCasesQuery({ search }, { refetchOnMountOrArgChange: true });
	const [escalateSupportCase, { isLoading: isEscalating }] = useEscalateSupportCaseMutation();
	const [closeSupportCase] = useCloseSupportCaseMutation();
	const cases = data?.cases ?? [];
	const displayedCaseCount = data?.returnedRecords ?? cases.length;

	const handleEscalateCase = async (reason: string) => {
		if (!caseToEscalate?.id) return;

		setActionCaseId(caseToEscalate.id);

		try {
			await escalateSupportCase({ caseId: caseToEscalate.id, request: { reason } }).unwrap();
			setCaseToEscalate(undefined);
		} finally {
			setActionCaseId(undefined);
		}
	};

	const handleCloseCase = async (supportCase: SupportCase) => {
		if (!supportCase.id) return;

		setActionError(false);
		setActionCaseId(supportCase.id);

		try {
			await closeSupportCase({ caseId: supportCase.id }).unwrap();
		} catch {
			setActionError(true);
		} finally {
			setActionCaseId(undefined);
		}
	};

	return (
		<Styles.Section aria-labelledby="support-cases-heading">
			<Styles.SectionHeader>
				<Styles.Heading id="support-cases-heading">Support Cases</Styles.Heading>

				<Styles.SubmitRequestButton
					onClick={() => navigate("/support/request")}
					variant="contained"
				>
					Submit a Support Request
				</Styles.SubmitRequestButton>
			</Styles.SectionHeader>

			<Styles.CasesPanel>
				<Styles.CasesToolbar>
					<Styles.CaseSummary>
						<Styles.Count>
							{displayedCaseCount} Support {displayedCaseCount === 1 ? "Case" : "Cases"} Opened in Last 30
							Days
						</Styles.Count>
						<Styles.AwaitingReplyNote>
							For cases “Awaiting Your Reply” please check your email with necessary next steps.
						</Styles.AwaitingReplyNote>
					</Styles.CaseSummary>

					<Styles.SearchField
						error={hasSearchError}
						helperText={hasSearchError ? "Enter at least 3 characters." : undefined}
						onChange={(event) => setSearchInput(event.target.value)}
						placeholder="Search Your Support Cases"
						size="small"
						slotProps={{
							input: {
								endAdornment: (
									<InputAdornment position="end">
										<SearchIcon />
									</InputAdornment>
								)
							},
							htmlInput: { "aria-label": "Search support cases" }
						}}
						value={searchInput}
					/>
				</Styles.CasesToolbar>

				{actionError && (
					<Styles.ActionError>
						<Alert severity="error">Unable to close this case. Please try again.</Alert>
					</Styles.ActionError>
				)}

				{isFetching && <TableSkeleton />}

				{isError && !isFetching && (
					<ErrorBanner errorMessaging="Unable to load support cases. Please try again later." />
				)}

				{!isFetching && !isError && cases.length === 0 && (
					<Styles.EmptyMessage>No support cases were found.</Styles.EmptyMessage>
				)}

				{!isFetching && !isError && cases.length > 0 && (
					<SupportCasesTable
						actionCaseId={actionCaseId}
						cases={cases}
						onCloseCase={(supportCase) => void handleCloseCase(supportCase)}
						onEscalateCase={setCaseToEscalate}
					/>
				)}
			</Styles.CasesPanel>

			<EscalateCaseDialog
				isLoading={isEscalating}
				onClose={() => setCaseToEscalate(undefined)}
				onConfirm={handleEscalateCase}
				open={Boolean(caseToEscalate)}
			/>
		</Styles.Section>
	);
};

export default SupportCases;
